// Chapter 12 - Negative Reinforcement
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 12 (Smith & Iwata).
// All content traced to source-pdfs/Chapter-12-Negative-Reinforcement.pdf

import type { ChapterModule } from "./types";

const chapter12Module: ChapterModule = {
  meta: {
    id: "12",
    number: 12,
    title: "Negative Reinforcement",
    subtitle: "Escape vs avoidance, SR-, free-operant avoidance",
    part: "Part 4 - Reinforcement",
    estMinutes: 70,
    bacb: ["B-4", "G-1", "G-4"],
  },
  curated: true,
  overview: [
    "Chapter 12 (Smith & Iwata) tackles one of the most consistently misunderstood principles in behavior analysis. Negative reinforcement involves the termination, reduction, or postponement of a stimulus contingent on a response, which INCREASES the future occurrence of that response. Both positive and negative reinforcement increase behavior; they differ only in whether the consequence is stimulus PRESENTATION (SR+) or stimulus TERMINATION (SR-).",
    "The chapter distinguishes escape (terminate an ongoing stimulus) from avoidance (delay or prevent stimulus presentation), and discriminated from free-operant avoidance. It explores how negative reinforcement drives both academic learning and escalating problem behavior in classrooms, the symmetrical caregiver-student trap, and the ethical considerations that come with arranging aversive antecedents.",
  ],
  objectives: [
    { id: "B-4", text: "Define and provide examples of positive and negative reinforcement contingencies." },
    { id: "G-1", text: "Use negative reinforcement procedures appropriately and ethically." },
    { id: "G-4", text: "Address problem behavior maintained by escape via extinction and replacement reinforcement." },
  ],
  coreConcepts: [
    {
      title: "Definition and contingency",
      body: [
        "Negative reinforcement involves the termination, reduction, or postponement of a stimulus contingent on a response, leading to an INCREASE in the future occurrence of that response. The contingency has four elements: (a) an establishing operation (EO) that makes escape reinforcing, (b) a discriminative stimulus (SD) in whose presence the response is more likely to be reinforced, (c) the response that produces reinforcement, and (d) termination of the event that served as the EO.",
      ],
    },
    {
      title: "Negative reinforcement vs positive reinforcement vs punishment",
      body: [
        "Positive and negative reinforcement are SIMILAR: both increase responding. They DIFFER in operation: positive reinforcement involves contingent stimulus PRESENTATION; negative reinforcement involves contingent stimulus TERMINATION. Negative reinforcement and punishment DIFFER on two axes: (a) operation (termination vs presentation) and (b) effect (increase vs decrease).",
      ],
    },
    {
      title: "Escape vs avoidance",
      body: [
        "An escape contingency: responding TERMINATES an ongoing stimulus. An avoidance contingency: responding DELAYS or PREVENTS the presentation of a stimulus. In DISCRIMINATED avoidance, a signal precedes the aversive stimulus; responding in the presence of the signal prevents the stimulus. In FREE-OPERANT avoidance, responding at any time prevents the stimulus; there is no specific signal.",
      ],
    },
    {
      title: "What can be a negative reinforcer",
      body: [
        "Any stimulus whose termination strengthens behavior can serve as a negative reinforcer. Unconditioned negative reinforcers strengthen behavior on removal without prior learning. Conditioned negative reinforcers acquire that function through previous pairing with other negative reinforcers. Social negative reinforcement involves stimulus termination through another person's action; automatic negative reinforcement involves termination as a direct result of the response.",
      ],
    },
    {
      title: "Identifying negative reinforcers",
      body: [
        "Identification requires specifying the stimulus conditions BEFORE and AFTER responding. If conditions improve (an aversive event ends) following the response and the response increases, you have a candidate negative reinforcer.",
      ],
    },
    {
      title: "Factors that increase effectiveness",
      body: [
        "Negative reinforcement is more effective when (a) the stimulus change immediately follows the target response, (b) the magnitude of reinforcement is large, (c) the target response consistently produces escape from or postponement of the EO, and (d) reinforcement is unavailable for competing responses.",
      ],
    },
    {
      title: "How negative reinforcement drives learning AND problem behavior",
      body: [
        "Negative reinforcement plays an important role in academic-skill development (error correction, practice-to-mastery) but ALSO accounts for the development of disruptive or dangerous behavior. Task demands during instruction can function as EOs for escape; initial escape may take the form of inattention or mild disruption. When positive reinforcement for compliance is sub-optimal, escape behaviors persist and may escalate.",
      ],
    },
    {
      title: "Extinction and replacement for escape behavior",
      body: [
        "Extinction of behavior maintained by negative reinforcement requires that responding NOT be allowed to avoid or terminate its programmed consequence (often called 'escape extinction'). A strategy for treating escape-maintained problem behavior is to strengthen a more socially appropriate replacement behavior through negative reinforcement (e.g., teach a break-request that earns escape).",
      ],
    },
    {
      title: "The caregiver-student trap",
      body: [
        "Negative reinforcement operates symmetrically. Student problem behavior serves as an EO for teacher escape and avoidance; teacher responses to problem behavior (e.g., backing off the demand) are maintained by negative reinforcement. The cycle perpetuates both the student's escape behavior AND the teacher's avoidant management practices.",
      ],
    },
    {
      title: "Ethical considerations",
      body: [
        "Most EOs for behavior maintained by negative reinforcement are AVERSIVE events. Severely noxious events as antecedents cannot be justified in a typical behavior-change program. Aversive stimuli also generate competing behaviors that interfere with the acquisition of desired behavior. Positive-reinforcement-based interventions can be effective for problem behavior even when the behavior is maintained by negative reinforcement.",
      ],
    },
  ],
  glossary: [
    { term: "Negative reinforcement", definition: "Termination, reduction, or postponement of a stimulus contingent on a response, leading to an increase in the future occurrence of that response.", sourceHint: "Negative reinforcement involves the termination, reduction, or postponement of a stimulus contingent on the occurrence of a response, which leads to an increase in the future occurrence of that response." },
    { term: "Escape contingency", definition: "A contingency in which responding TERMINATES an ongoing stimulus.", sourceHint: "An escape contingency is one in which responding terminates an ongoing stimulus." },
    { term: "Avoidance contingency", definition: "A contingency in which responding DELAYS or PREVENTS the presentation of a stimulus.", sourceHint: "An avoidance contingency is one in which responding delays or prevents the presentation of a stimulus." },
    { term: "Discriminated avoidance", definition: "An avoidance contingency in which responding in the presence of a SIGNAL prevents the stimulus presentation.", sourceHint: "In discriminated avoidance, responding in the presence of a signal prevents stimulus presentation." },
    { term: "Free-operant avoidance", definition: "An avoidance contingency without a specific signal; responding at any time prevents stimulus presentation.", sourceHint: "in free-operant avoidance, responding at any time prevents stimulus presentation." },
    { term: "Unconditioned negative reinforcer", definition: "A stimulus whose removal strengthens behavior without prior learning history.", sourceHint: "Unconditioned negative reinforcers are stimuli whose removal strengthens behavior in the absence of prior learning." },
    { term: "Conditioned negative reinforcer", definition: "A stimulus whose removal strengthens behavior as a result of previous pairing with other negative reinforcers.", sourceHint: "Conditioned negative reinforcers are stimuli whose removal strengthens behavior as a result of previous pairing with other negative reinforcers." },
    { term: "Social negative reinforcement", definition: "Negative reinforcement in which another person terminates the stimulus.", sourceHint: "Social negative reinforcement involves stimulus termination through the action of another person." },
    { term: "Automatic negative reinforcement", definition: "Negative reinforcement in which stimulus termination is a direct result of the response, without social mediation.", sourceHint: "Automatic negative reinforcement involves stimulus termination as a direct result of a response." },
    { term: "Escape extinction", definition: "Extinction of escape-maintained behavior in which responding is no longer permitted to avoid or terminate the aversive consequence.", sourceHint: "Extinction of behavior maintained by negative reinforcement involves not allowing a response to avoid or terminate its programmed consequence." },
    { term: "Establishing operation (EO) for escape", definition: "An aversive antecedent that makes the termination of the aversive event reinforcing.", sourceHint: "A negative reinforcement contingency involves (a) an establishing operation (EO) in whose presence escape is reinforcing." },
  ],
  procedures: [
    {
      title: "Identify a negative reinforcement contingency",
      intro: "Specify the stimulus conditions before and after responding.",
      steps: [
        { title: "Identify the aversive antecedent (EO)", detail: "What stimulus or event is present when the behavior occurs? Is it likely aversive?" },
        { title: "Specify the response", detail: "Operationally define what the person does." },
        { title: "Specify the consequence", detail: "Does the response TERMINATE, reduce, or postpone the aversive event?" },
        { title: "Check for an increase", detail: "Does the response occur more often when the aversive antecedent recurs?" },
        { title: "Classify", detail: "Escape (terminates ongoing stimulus) or avoidance (delays/prevents the stimulus)? Discriminated or free-operant?" },
      ],
    },
    {
      title: "Treat escape-maintained problem behavior",
      intro: "Combine escape extinction with replacement reinforcement.",
      steps: [
        { title: "Functionally identify the behavior", detail: "Confirm the behavior is maintained by escape via FBA (see Chapter 27)." },
        { title: "Implement escape extinction", detail: "Do not allow problem behavior to terminate or postpone the task demand." },
        { title: "Teach a replacement", detail: "Teach a more appropriate communication (e.g., 'break please') that produces brief escape contingent on the new response." },
        { title: "Thin escape schedule", detail: "Gradually require more work before granting escape; pair task completion with positive reinforcers." },
        { title: "Monitor caregiver behavior", detail: "Coach implementers to avoid being trapped in the symmetrical negative-reinforcement cycle." },
      ],
    },
  ],
  examples: [
    { scenario: "Headache; person takes ibuprofen; headache subsides; future use of ibuprofen for headache increases.",
      application: "Escape contingency. Stimulus (headache) terminated by response (ibuprofen), behavior increases.",
      sourceHint: "An escape contingency is one in which responding terminates an ongoing stimulus." },
    { scenario: "Walking sign turns red; pedestrian checks for cars before crossing to avoid an oncoming car; checking increases.",
      application: "Discriminated avoidance. Signal (red light) precedes the aversive stimulus; the avoidance response prevents the stimulus.",
      sourceHint: "In discriminated avoidance, responding in the presence of a signal prevents stimulus presentation." },
    { scenario: "Teacher gives math worksheet; student screams; teacher removes worksheet; student's screaming increases when worksheets appear.",
      application: "Negative reinforcement maintaining problem behavior. Symmetrically, the teacher's worksheet-removal is also negatively reinforced.",
      sourceHint: "Task demands during instruction may function as an EO for escape; teacher responses to problem behavior are maintained by negative reinforcement." },
    { scenario: "Student learns to ask 'Break please' and earns 30 seconds away from the task; screaming decreases.",
      application: "Replacement behavior maintained by negative reinforcement (functional communication training).",
      sourceHint: "One strategy for treating problem behaviors maintained by negative reinforcement is to strengthen a more socially appropriate replacement behavior through negative reinforcement." },
  ],
  comparisons: [
    {
      title: "Positive vs Negative reinforcement",
      left:  { label: "Positive reinforcement", points: [
        "Contingent stimulus PRESENTATION.",
        "Behavior INCREASES.",
        "Example: praise, food, money.",
      ]},
      right: { label: "Negative reinforcement", points: [
        "Contingent stimulus TERMINATION.",
        "Behavior INCREASES.",
        "Example: pain relief, escape from demands.",
      ]},
    },
    {
      title: "Negative reinforcement vs Punishment",
      left:  { label: "Negative reinforcement", points: [
        "Operation: stimulus TERMINATION.",
        "Effect: behavior INCREASES.",
      ]},
      right: { label: "Punishment", points: [
        "Operation: stimulus PRESENTATION (or termination, for SP-).",
        "Effect: behavior DECREASES.",
      ]},
    },
    {
      title: "Escape vs Avoidance",
      left:  { label: "Escape", points: [
        "Stimulus is ALREADY present.",
        "Response terminates the stimulus.",
      ]},
      right: { label: "Avoidance", points: [
        "Response DELAYS or PREVENTS the stimulus.",
        "Discriminated (signaled) or free-operant (unsignaled).",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Negative reinforcement means punishment.",
      reality: "Negative reinforcement INCREASES behavior. 'Negative' refers to removing a stimulus, not to punishment.",
      sourceHint: "Negative reinforcement involves the termination, reduction, or postponement of a stimulus contingent on the occurrence of a response, which leads to an increase in the future occurrence of that response.",
    },
    {
      myth: "Avoidance and escape are the same thing.",
      reality: "Escape terminates an ongoing stimulus; avoidance delays or prevents stimulus presentation entirely.",
      sourceHint: "An escape contingency is one in which responding terminates an ongoing stimulus. An avoidance contingency is one in which responding delays or prevents the presentation of a stimulus.",
    },
    {
      myth: "Removing the aversive stimulus is enough to extinguish escape behavior.",
      reality: "Escape extinction requires that the response no longer be allowed to avoid or terminate the aversive event; just removing the event reinforces, not extinguishes.",
      sourceHint: "Extinction of behavior maintained by negative reinforcement involves not allowing a response to avoid or terminate its programmed consequence.",
    },
    {
      myth: "Problem behavior maintained by escape requires aversive treatment.",
      reality: "Interventions based on POSITIVE reinforcement can be effective for problem behavior maintained by negative reinforcement.",
      sourceHint: "Results of some research have shown that interventions based on positive reinforcement also can be effective as treatment for problem behavior maintained by negative reinforcement.",
    },
    {
      myth: "Negative reinforcement is mostly used in research; it has no role in everyday teaching.",
      reality: "Negative reinforcement plays an important role in academic-skill development AND in the development of disruptive behavior. Many error-correction procedures function via negative reinforcement.",
      sourceHint: "Negative reinforcement may play an important role in the development of academic skills, but it can also account for the development of disruptive or dangerous behavior.",
    },
  ],
  examChecklist: [
    "Define negative reinforcement with all four contingency elements.",
    "State two ways positive and negative reinforcement are similar and two ways they differ.",
    "Define escape and avoidance contingencies.",
    "Distinguish discriminated and free-operant avoidance.",
    "Distinguish unconditioned and conditioned negative reinforcers.",
    "Distinguish social and automatic negative reinforcement.",
    "Describe extinction of escape-maintained behavior.",
    "Describe the symmetrical caregiver-student negative-reinforcement trap.",
    "List four factors that increase effectiveness of negative reinforcement.",
    "Explain ethical concerns about using aversive antecedents as EOs.",
  ],
  flashcardTerms: [
    "Negative reinforcement",
    "Escape contingency", "Avoidance contingency",
    "Discriminated avoidance", "Free-operant avoidance",
    "Unconditioned negative reinforcer", "Conditioned negative reinforcer",
    "Social negative reinforcement", "Automatic negative reinforcement",
    "Escape extinction",
    "Establishing operation (EO) for escape",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which best describes negative reinforcement?",
      options: [
        { label: "A", text: "Presenting a stimulus that decreases behavior" },
        { label: "B", text: "Removing a stimulus that decreases behavior" },
        { label: "C", text: "Removing a stimulus that increases behavior" },
        { label: "D", text: "Presenting a stimulus that increases behavior" },
      ],
      answer: "C",
      explanation: "Negative reinforcement REMOVES (terminates, reduces, or postpones) a stimulus and the behavior INCREASES. Option D is positive reinforcement.",
      topic: "Definition",
      sourceHint: "Negative reinforcement involves the termination, reduction, or postponement of a stimulus contingent on the occurrence of a response, which leads to an increase in the future occurrence of that response.",
    },
    {
      id: "Q2",
      question: "A pedestrian checks for cars before crossing the street whenever the walk sign is red. This is an example of:",
      options: [
        { label: "A", text: "Escape" },
        { label: "B", text: "Discriminated avoidance" },
        { label: "C", text: "Free-operant avoidance" },
        { label: "D", text: "Punishment" },
      ],
      answer: "B",
      explanation: "A signal (red light) precedes the potential aversive event; responding in the presence of the signal prevents it - discriminated avoidance.",
      topic: "Escape vs avoidance",
      sourceHint: "In discriminated avoidance, responding in the presence of a signal prevents stimulus presentation.",
    },
    {
      id: "Q3",
      question: "When a teacher removes a worksheet after a student screams, and the student then screams whenever worksheets appear, the screaming is being maintained by:",
      options: [
        { label: "A", text: "Positive reinforcement (attention)" },
        { label: "B", text: "Negative reinforcement (escape from demand)" },
        { label: "C", text: "Positive punishment" },
        { label: "D", text: "Automatic reinforcement" },
      ],
      answer: "B",
      explanation: "The aversive stimulus (worksheet/demand) is terminated contingent on screaming; screaming increases under the demand. Classic escape-maintained problem behavior.",
      topic: "Applied negative reinforcement",
      sourceHint: "Task demands during instruction may function as an EO for escape.",
    },
    {
      id: "Q4",
      question: "Escape extinction requires:",
      options: [
        { label: "A", text: "Removing the aversive antecedent permanently" },
        { label: "B", text: "Allowing the response to terminate the aversive event a few times" },
        { label: "C", text: "Not allowing the response to avoid or terminate the programmed consequence" },
        { label: "D", text: "Increasing the magnitude of the aversive event" },
      ],
      answer: "C",
      explanation: "Escape extinction holds the aversive event in place; the response no longer produces escape, and the behavior decreases.",
      topic: "Escape extinction",
      sourceHint: "Extinction of behavior maintained by negative reinforcement involves not allowing a response to avoid or terminate its programmed consequence.",
    },
    {
      id: "Q5",
      question: "Which is an UNCONDITIONED negative reinforcer?",
      options: [
        { label: "A", text: "A grade of C on a test" },
        { label: "B", text: "Withdrawal of social attention" },
        { label: "C", text: "Painful stimulation that is removed" },
        { label: "D", text: "Loss of access to a token" },
      ],
      answer: "C",
      explanation: "Painful stimulation strengthens removal behavior without prior learning - an unconditioned negative reinforcer.",
      topic: "Classifying negative reinforcers",
      sourceHint: "Unconditioned negative reinforcers are stimuli whose removal strengthens behavior in the absence of prior learning.",
    },
    {
      id: "Q6",
      question: "Why is it important to teach a replacement behavior when treating escape-maintained problem behavior?",
      options: [
        { label: "A", text: "To create a positive reinforcement contingency that competes with the problem behavior." },
        { label: "B", text: "To prevent the EO from re-emerging." },
        { label: "C", text: "To provide an appropriate response that produces escape, replacing the problem behavior." },
        { label: "D", text: "To eliminate the need for extinction." },
      ],
      answer: "C",
      explanation: "FCT-style replacement teaches a socially appropriate response (e.g., 'break please') that produces the same reinforcement (escape).",
      topic: "Treatment",
      sourceHint: "One strategy for treating problem behaviors maintained by negative reinforcement is to strengthen a more socially appropriate replacement behavior through negative reinforcement.",
    },
    {
      id: "Q7",
      question: "Caregiver-student cycles often involve symmetrical negative reinforcement because:",
      options: [
        { label: "A", text: "Both parties find each other intrinsically rewarding." },
        { label: "B", text: "Student problem behavior is an EO for teacher escape, and teacher demand-withdrawal is negatively reinforced." },
        { label: "C", text: "Punishment is unavoidable." },
        { label: "D", text: "Caregivers always reinforce positively." },
      ],
      answer: "B",
      explanation: "Both behaviors are maintained by escape from the other party's aversive behavior, perpetuating the cycle.",
      topic: "Caregiver dynamics",
      sourceHint: "Student problem behavior serves as an EO teacher escape and avoidance, and teacher responses to problem behavior are maintained by negative reinforcement.",
    },
  ],
  reviewPrompts: [
    "Diagram a four-element negative reinforcement contingency (EO, SD, R, removal of the EO).",
    "Identify one escape and one avoidance contingency in your daily life.",
    "Describe a classroom situation where escape extinction is used and how a replacement behavior is taught.",
    "Explain why an aversive antecedent should not be added to a typical behavior change program.",
    "Apply factors that increase effectiveness to a negative reinforcement intervention you know.",
  ],
  recap: [
    "Negative reinforcement increases behavior by terminating, reducing, or postponing a stimulus. It has four elements: EO, SD, response, and stimulus termination.",
    "Escape ends an ongoing stimulus; avoidance prevents or delays one. Discriminated avoidance uses a signal; free-operant avoidance does not.",
    "Negative reinforcement drives both adaptive learning and escape-maintained problem behavior. Treatment combines escape extinction with replacement behavior taught via more appropriate negative reinforcement, often supported by positive reinforcement for compliance. Aversive antecedents raise ethical concerns and can generate competing behaviors that interfere with learning.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition and contingency",
      text: "Negative reinforcement involves the termination, reduction, or postponement of a stimulus contingent on the occurrence of a response, which leads to an increase in the future occurrence of that response. A negative reinforcement contingency involves (a) an establishing operation (EO) in whose presence escape is reinforcing, (b) a discriminative stimulus (SD) in whose presence a response is more likely to be reinforced, (c) the response that produces reinforcement, and (d) termination of the event that served as the EO.",
    },
    {
      heading: "Escape vs avoidance",
      text: "An escape contingency is one in which responding terminates an ongoing stimulus. An avoidance contingency is one in which responding delays or prevents the presentation of a stimulus. In discriminated avoidance, responding in the presence of a signal prevents stimulus presentation; in free-operant avoidance, responding at any time prevents stimulus presentation.",
    },
    {
      heading: "Applied use and the classroom cycle",
      text: "Negative reinforcement may play an important role in the development of academic skills, but it can also account for the development of disruptive or dangerous behavior. The presentation of task demands during instruction may function as an EO for escape; initial forms of escape may include lack of attention or mild forms of disruption. Student problem behavior serves as an EO for teacher escape and avoidance, and teacher responses to problem behavior are maintained by negative reinforcement.",
    },
    {
      heading: "Ethics",
      text: "Most EOs for behavior maintained by negative reinforcement can be viewed as aversive events. Extremely noxious events, when presented as antecedent stimuli, cannot be justified as part of a typical behavior change program. Another concern with negative reinforcement is that the presence of aversive stimuli can itself generate behaviors that compete with the acquisition of desired behavior.",
    },
  ],
};

export default chapter12Module;
